<script setup>
import {computed, getCurrentInstance} from 'vue'

const props = defineProps({
    editor: {
        type: Object,
        required: true
    }
})

const {proxy} = getCurrentInstance()
const store = proxy.$store

// --- Computed ---

const isMac = computed(() => {
    const platform = navigator.userAgentData?.platform || navigator.platform || ''
    return /Mac|iPhone|iPod|iPad/.test(platform)
})

const isDarkMode = computed(() => store.getters['user/pref']('darkMode'))

const modKey = computed(() => isMac.value ? '⌘' : 'Ctrl')
const optKey = computed(() => isMac.value ? '⌥' : 'Alt')
</script>

<template>
    <div class="btn-group btn-group-sm" role="group" aria-label="Paragraph format">
        <div class="dropdown">
            <button class="btn btn-sm dropdown-toggle" :class="{'btn-light': !isDarkMode}" type="button"
                    id="paragraphFormatDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                <span v-if="editor.isActive('heading', { level: 1 })">Heading 1</span>
                <span v-else-if="editor.isActive('heading', { level: 2 })">Heading 2</span>
                <span v-else-if="editor.isActive('heading', { level: 3 })">Heading 3</span>
                <span v-else-if="editor.isActive('heading', { level: 4 })">Heading 4</span>
                <span v-else-if="editor.isActive('heading', { level: 5 })">Heading 5</span>
                <span v-else-if="editor.isActive('heading', { level: 6 })">Heading 6</span>
                <span v-else-if="editor.isActive('paragraph')">Paragraph</span>
                <span v-else-if="editor.isActive('codeBlock')">Code block</span>
                <span v-else>Format</span>
            </button>
            <ul class="dropdown-menu" aria-labelledby="paragraphFormatDropdown">
                <li>
                    <a class="dropdown-item" :class="{ 'active': editor.isActive('paragraph') }"
                       @click="editor.commands.setParagraph()">
                        <i class="bi bi-text-paragraph me-1"></i> <small>Paragraph</small>
                    </a>
                </li>

                <li v-for="level in 6" :key="level">
                    <a class="dropdown-item d-flex justify-content-between align-items-center"
                       :class="{ 'active': editor.isActive('heading', { level }) }"
                       @click="editor.commands.toggleHeading({ level })">
                        <i :class="`bi bi-type-h${level} me-1`"></i> <small>Heading</small>
                        <span class="text-muted ms-5 small">{{ modKey + ' ' + optKey + ' ' + level }}</span>
                    </a>
                </li>

                <li>
                    <hr class="dropdown-divider">
                </li>

                <li>
                    <a class="dropdown-item d-flex justify-content-between align-items-center"
                       :class="{ 'active': editor.isActive('codeBlock') }"
                       @click="editor.commands.toggleCodeBlock()">
                        <i class="bi bi-file-earmark-code me-1"></i> <small>Code block</small>
                        <span class="text-muted ms-5 small">```</span>
                    </a>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped>
.dropdown-divider {
    /* Override the divider's background color variable with the global theme border color */
    --bs-dropdown-divider-bg: var(--bs-border-color);

    /* Bootstrap uses opacity on dividers; set to 1 to match standard borders perfectly */
    opacity: 1;
}
</style>